import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { withSize } from 'react-sizeme';

import MessagePropType from '../definitions/MessagePropType';
import messageToString from '../functions/messageToString';
import useProductCardLayout from '../hooks/useProductCardLayout';


function ProductCardName({ designationMessage, image, name, children }) {
    return (
        /* CSS styling for these elements are provided by ProductCardLayout */
        <div className="row rh-display-flex rh-flex-wrap">
            <div className="name-wrapper rh-mb-0_5">
                {/* we must wrap the name part, not the flex-item */}
                <MyNameWithSize
                    designationMessage={designationMessage}
                    image={image}
                    name={name}
                />
            </div>

            <div className="top-right-wrapper rh-box-sizing-border-box">
                <MyTopRightWithSize>
                    {children}
                </MyTopRightWithSize>
            </div>
        </div>
    );
}

ProductCardName.propTypes = {
    designationMessage: PropTypes.node,

    image: PropTypes.shape({
        url: PropTypes.string.isRequired,
        alt: MessagePropType,
        width: PropTypes.string.isRequired,
        height: PropTypes.string,
    }),

    name: PropTypes.shape({
        message: MessagePropType,
        Component: PropTypes.node,
    }).isRequired,

    children: PropTypes.node,
};


const WITH_SIZE_OPTIONS = { monitorHeight: true, noPlaceholder: true };

// eslint-disable-next-line react/no-multi-comp
const MyNameWithSize = withSize(WITH_SIZE_OPTIONS)(
    function({ size, designationMessage, image, name }) {
        const intl = useIntl();
        const measurements = useProductCardLayout();

        useEffect(
            () => {
                size.height && measurements.setNameHeight(size.height);
            },
            [ size.height ],
        );

        return (
            <div className="product-card-name rh-display-flex rh-flex-direction-column rh-align-items-stretch rh-pb-0_5">
                <If condition={designationMessage}>
                    <span
                        className="rh-display-inline-block rh-text-xs rh-mb-0_5 rh-text-lowercase"
                        data-test-name="designation-message"
                    >
                        {designationMessage}
                    </span>
                </If>

                <If condition={image}>
                    <img
                        className="product-card-name-logo rh-mr-1"
                        src={image.url}
                        alt={messageToString(image.alt, intl) || ''}
                    />
                </If>

                <Choose>
                    <When condition={name.message}>
                        <h3 className="rh-title-m rh-m-0">
                            {messageToString(name.message, intl)}
                        </h3>
                    </When>

                    <Otherwise>
                        {name.Component}
                    </Otherwise>
                </Choose>
            </div>
        );
    },
);

MyNameWithSize.propTypes = ({
    size: PropTypes.shape({
        height: PropTypes.number.isRequired,
    }),

    designationMessage: PropTypes.node,

    image: PropTypes.shape({
        url: PropTypes.string.isRequired,
        alt: MessagePropType,
        width: PropTypes.string.isRequired,
        height: PropTypes.string,
    }),

    name: PropTypes.shape({
        message: MessagePropType,
        Component: PropTypes.node,
    }).isRequired,
});


// eslint-disable-next-line react/no-multi-comp
const MyTopRightWithSize = withSize(WITH_SIZE_OPTIONS)(
    function({ size, children }) {
        const measurements = useProductCardLayout();

        useEffect(
            () => {
                size.height && measurements.setTopRightHeight(size.height);
            },
            [ size.height ],
        );

        // fragment required due to use of withSize()
        return (
            <>
                {children}
            </>
        );
    },
);

MyTopRightWithSize.propTypes = {
    size: PropTypes.shape({
        height: PropTypes.number.isRequired,
    }),
    children: PropTypes.node,
};


export default ProductCardName;
